import React, { useRef } from "react";
import { Col, Row } from "react-bootstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Localize } from "../../app/lang/langs";
import moment from "moment";
import styles from "./ContractsView.module.css";
import {
  dateBodyTemplate,
  dateFilterTemplate,
  statusBodyTemplate,
  typeCollabBodyTemplate,
  statusItemTemplate,
  companiesItemTemplate,
  createDropdownFilterTemplate,
  createMultiSelectFilterTemplate,
  contractTypeFilterFunction,
  motifSortieFilterFunction,
  typeOfContractStrings,
  typesOfMotifSortie,
} from "./contractsTableUtils";
import { SILAE_CONTRACT_TABLE_FIELDS } from "../collabs/collabsTableUtils";
import { SilaeLegend } from "../../components/collabs/SilaeLegend";

export default function ContractsDataTable({
  data,
  filters,
  globalFilterValue,
  onGlobalFilterChange,
  onRowDoubleClick,
  onValueChange,
  scrollHeight,
  statuses,
  companies,
  collabsTypes,
}) {
  const dtRef = useRef(null);
  const date = moment().format("DD-MM-YYYY");

  const tableHeader = () => (
    <Row>
      <Col md={6}>
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Keyword Search"
          />
        </IconField>
      </Col>
      <Col md={6} className="d-flex justify-content-end align-items-center">
        <SilaeLegend />
      </Col>
    </Row>
  );

  // Extra props for columns whose values are synced from Silae (internes only)
  const silaeColumnProps = (field) =>
    SILAE_CONTRACT_TABLE_FIELDS.has(field)
      ? {
          headerClassName: styles.silaeHeader,
          headerTooltip: Localize("SILAE_SYNCED_FIELD"),
          bodyClassName: (rowData) =>
            rowData.type_collab === "interne" ? styles.silaeCell : "",
        }
      : {};

  const statusRowFilterTemplate = createDropdownFilterTemplate(statuses, statusItemTemplate);
  const companyRowFilterTemplate = createDropdownFilterTemplate(companies, companiesItemTemplate);
  const collabsTypeRowFilterTemplate = createDropdownFilterTemplate(collabsTypes, statusItemTemplate);
  const contractTypeRowFilterTemplate = createMultiSelectFilterTemplate(typeOfContractStrings);
  const motifSortieRowFilterTemplate = createMultiSelectFilterTemplate(typesOfMotifSortie);

  return (
    <div className={styles.collabsCard}>
      <DataTable
        ref={dtRef}
        exportFilename={`Collabs_export_${date}`}
        onValueChange={onValueChange}
        value={data}
        dataKey="id"
        paginator
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        rowsPerPageOptions={[10, 25, 50]}
        rows={25}
        scrollable
        scrollHeight={scrollHeight}
        sortMode="single"
        filters={filters}
        currentPageReportTemplate={`{first} ${Localize("to")} {last} ${Localize("of")} {totalRecords}`}
        onRowDoubleClick={onRowDoubleClick}
        selectionMode="single"
        header={tableHeader}
      >
        <Column field="nom" {...silaeColumnProps("nom")} filter filterPlaceholder="nom" header={Localize("Family Name")} sortable dataType="text" style={{ minWidth: "9rem" }} />
        <Column field="prenom" {...silaeColumnProps("prenom")} filter filterPlaceholder="prenom" header={Localize("Name")} sortable style={{ minWidth: "9rem" }} />
        <Column field="company" {...silaeColumnProps("company")} header={Localize("Company")} sortable style={{ minWidth: "8rem" }} filterField="company" showFilterMatchModes={false} filter filterElement={companyRowFilterTemplate} />
        <Column field="type_collab" {...silaeColumnProps("type_collab")} header={Localize("Type")} body={typeCollabBodyTemplate} sortable style={{ minWidth: "8rem" }} showFilterMatchModes={false} filter filterElement={collabsTypeRowFilterTemplate} />
        <Column field="statut" header={Localize("Status")} sortable body={statusBodyTemplate} style={{ minWidth: "7rem" }} showFilterMatchModes={false} filter filterElement={statusRowFilterTemplate} />
        <Column field="type_contrat" {...silaeColumnProps("type_contrat")} header={Localize("CONTRACT_TYPE")} showFilterMatchModes={false} filter filterFunction={contractTypeFilterFunction} filterElement={contractTypeRowFilterTemplate} sortable style={{ minWidth: "10rem" }} />
        <Column field="nature_emploi" {...silaeColumnProps("nature_emploi")} header={Localize("Nature emploi")} filter sortable style={{ minWidth: "8rem" }} />
        <Column field="etablissement" {...silaeColumnProps("etablissement")} header={Localize("Etablissement de rattachement")} filter sortable style={{ minWidth: "8rem" }} />
        <Column field="exitDate" {...silaeColumnProps("exitDate")} header={Localize("Date de fin de du contrat en cours")} body={dateBodyTemplate} sortable filter filterElement={dateFilterTemplate} dataType="date" style={{ minWidth: "10rem" }} />
        <Column field="motif_sortie" header={Localize("Motif de la sortie effective")} sortable style={{ minWidth: "10rem" }} filter showFilterMatchModes={false} filterElement={motifSortieRowFilterTemplate} filterFunction={motifSortieFilterFunction} />
        <Column field="date_debut_cp" header={Localize("date_debut_cp")} body={dateBodyTemplate} sortable filter filterElement={dateFilterTemplate} dataType="date" style={{ minWidth: "10rem" }} />
        <Column field="date_fin_cp" header={Localize("date_fin_cp")} body={dateBodyTemplate} sortable filter filterElement={dateFilterTemplate} dataType="date" style={{ minWidth: "10rem" }} />
        <Column field="new_date_fin_cp" header={Localize("new_date_fin_cp")} body={dateBodyTemplate} sortable filter filterElement={dateFilterTemplate} dataType="date" style={{ minWidth: "10rem" }} />
        <Column field="tjm_achat" header={Localize("tjm_achat")} sortable style={{ minWidth: "8rem" }} />
        <Column field="tjm_vente" header={Localize("tjm_vente")} sortable style={{ minWidth: "8rem" }} />
        <Column field="subcontractor" header={Localize("Subcontractor")} sortable style={{ minWidth: "8rem" }} />
        <Column field="subcontractor_email" header={Localize("Subcontractor Email")} sortable style={{ minWidth: "8rem" }} />
        <Column field="school" header={Localize("school")} sortable style={{ minWidth: "8rem" }} />
        <Column field="school_contact" header={Localize("school_contact")} sortable style={{ minWidth: "8rem" }} />
        <Column field="diploma" header={Localize("diploma")} sortable style={{ minWidth: "8rem" }} />
        <Column field="tutor" header={Localize("tutor")} sortable style={{ minWidth: "8rem" }} />
        <Column field="training_cost" header={Localize("training_cost")} sortable style={{ minWidth: "8rem" }} />
        <Column field="remaining_cost" header={Localize("remaining_cost")} sortable style={{ minWidth: "8rem" }} />
      </DataTable>
    </div>
  );
}
